"use client";

import { useQuery } from "@tanstack/react-query";
import { usePathname } from "next/navigation";
import { useEffect, useState } from "react";

import { DEFAULT_LANGUAGE } from "@/data/constants";
import { getLabel } from "@/data/labels";
import { fetchRecommendedNews } from "@/services/newsService";
import { ArticleType } from "@/types/article";
import { getLocaleFromPathname } from "@/utils/getLocaleFromPath";
import VerticalArticleCard from "../cards/VerticalArticleCard";
import SectionHeader from "./SectionHeader";

interface RecommendedNewsListProps {
  limit: number;
  repeatedArticleId?: string;
  language?: string;
  /** When set (e.g. Latest page next slice), skip `/recommended` API and render this list */
  prefetchedArticles?: ArticleType[];
}

export default function RecommendedNewsList({
  limit,
  repeatedArticleId,
  language = DEFAULT_LANGUAGE,
  prefetchedArticles,
}: RecommendedNewsListProps) {
  //
  const [articles, setArticles] = useState<ArticleType[] | undefined>();
  const pathname = usePathname();
  const locale = getLocaleFromPathname(pathname) || "en-IN";
  const langFromPath = (locale?.split("-")[0] || "en") as AppLocale;
  const langCode = langFromPath === "te" ? "te" : language === "te" ? "te" : "en";

  const usePrefetched = prefetchedArticles !== undefined;

  const { data } = useQuery({
    queryKey: ["fetchRecommendedNews", language, limit],
    enabled: !usePrefetched,
    queryFn: () =>
      fetchRecommendedNews({ limit: repeatedArticleId ? limit + 1 : limit, language: language }),
  });

  useEffect(() => {
    if (usePrefetched) {
      let list = prefetchedArticles ?? [];
      if (repeatedArticleId) {
        list = list.filter(
          article =>
            article?._id !== repeatedArticleId && article?.id !== repeatedArticleId,
        );
      }
      setArticles(list.length ? list : undefined);
      return;
    }
    if (repeatedArticleId) {
      const unRepeatedNewsList = data?.filter(
        article => article?._id !== repeatedArticleId && article?.id !== repeatedArticleId,
      );
      setArticles(unRepeatedNewsList);
    } else {
      setArticles(data);
    }
  }, [data, repeatedArticleId, usePrefetched, prefetchedArticles]);

  return (
    <div>
      <SectionHeader
        showLogo={false}
        title={getLabel(langCode, "RECOMMENDED")}
        iconBgColor="#F00"
      />
      <div className="flex flex-col gap-4 mt-8">
        {articles &&
          articles?.length > 0 &&
          articles?.map((article, index) => (
            <div key={index}>
              <VerticalArticleCard article={article} variant="lg" />
              <div className="pt-3 border-b"></div>
            </div>
          ))}
      </div>
    </div>
  );
}
